import { RoleType, AssessmentType, AssessmentStatus, getUserInitials, Decision } from '@promo/shared';
import type { Assessment, UpdateAssessmentDTO } from '@promo/shared';
import { useInvitations } from '../hooks/useInvitations';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useState } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { Skeleton } from '@/components/ui/skeleton';
import { UserInfo } from '@/components/common/UserInfo';
import { useUser } from '@/hooks/useUser';
import { useAssessments } from '@/features/assessments/hooks/useAssessments';
import { AssessmentForm } from './AssessmentForm';
import { getInitials } from '@promo/shared';
import type { SelectOption } from '@/components/ui/editable-select';
import { parseNameFromEmail } from '@promo/shared';

interface AssessmentsSectionProps {
  packetId: string;
  readOnly?: boolean;
  isManager?: boolean;
  canViewAssessments?: boolean;
}

interface AssessmentItemProps {
  item: Assessment;
  currentUserId: string;
  isEditable: boolean;
  isManager: boolean;
  updateAssessment: (id: string, data: UpdateAssessmentDTO) => void;
  deleteAssessment: (id: string) => void;
  isUpdating: boolean;
  isDeleting: boolean;
}
function AssessmentItem({
  item,
  currentUserId,
  isEditable,
  isManager,
  updateAssessment,
  deleteAssessment,
  isUpdating,
  isDeleting,
}: AssessmentItemProps) {
  const { data: user } = useUser(item.assessorId);
  const initials = user
    ? getUserInitials(user)
    : getInitials(`User ${item.assessorId.substring(0, 5)}...`, undefined);
  const name = user?.name ?? `User ${item.assessorId.substring(0, 5)}...`;

  // Determine editability based on user role and assessment status
  const isAssessorAndOwner = item.assessorId === currentUserId && !isManager;
  const isCompleted = item.status === AssessmentStatus.COMPLETED;
  const isDecisionDefer = item.decision === Decision.DEFER;

  // Overall form read-only state
  const formReadOnly = !isEditable || 
                     (isAssessorAndOwner && isCompleted && !isDecisionDefer) || 
                     (isManager && isCompleted);

  // Decision field specific read-only state
  const decisionFieldReadOnly = formReadOnly || // If form is RO, decision is RO
                                (isAssessorAndOwner && isCompleted && isDecisionDefer) ||
                                (isManager && isCompleted); 
                                
  // Deletion capability
  const canDelete = isEditable && isAssessorAndOwner && !isCompleted;

  // Title editing logic: own assessment or manager can edit any assessment
  const isOwnAssessment = item.assessorId === currentUserId;
  const showEditableTitle = (isOwnAssessment || isManager) && !!user;

  return (
    <div className="not-first:py-6 pb-10">
      {/* Assessment form */}
      <div className="mb-4">
        <AssessmentForm
          initialDecision={item.decision}
          initialJustification={item.justification}
          onSubmit={(data) => updateAssessment(item.id, {
            status: AssessmentStatus.COMPLETED,
            decision: data.decision,
            justification: data.justification,
          })}
          onCancel={() => {}}
          onDelete={canDelete ? () => deleteAssessment(item.id) : undefined}
          isSubmitting={isUpdating || isDeleting}
          readOnly={formReadOnly}
          decisionReadOnly={decisionFieldReadOnly}
        />
      </div>
      
      {/* User info row with responsive grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
        {/* Cell 1: Content area (hidden on mobile, 2/3 on desktop) */}
        <div className="hidden md:block md:col-span-3">
          {/* This space can be used for additional content if needed */}
        </div>
        
        {/* Cell 2: User info (full width on mobile, 1/3 on desktop) */}
        <div className="col-span-4 md:col-span-1 flex justify-start">
          <UserInfo 
            initials={initials} 
            name={name} 
            userId={user?.id}
            currentTitle={user?.title}
            editableTitle={showEditableTitle}
          />
        </div>
      </div>
    </div>
  );
}

export function AssessmentsSection({ packetId, readOnly, isManager = false, canViewAssessments = true }: AssessmentsSectionProps) {
  const { session } = useAuth();
  const currentUserId = session.user?.id ?? '';
  const isEditable = !readOnly;

  // Count invited assessors for progress
  const { data: assessorInvitations = [] } = useInvitations(packetId, RoleType.ASSESSOR);
  const invitedCount = assessorInvitations.length;

  // Manager override: selected assessor
  const [selectedBlindAssessorId, setSelectedBlindAssessorId] = useState<string>('');
  const [selectedUnblindAssessorId, setSelectedUnblindAssessorId] = useState<string>('');

  // Fetch both phases
  const {
    blinded,
    unblinded,
    isLoading,
    error,
    createAssessment,
    updateAssessment,
    deleteAssessment,
    isCreating,
    isUpdating,
    isDeleting,
  } = useAssessments(packetId, canViewAssessments);

  const [newBlindKey, setNewBlindKey] = useState(0);
  const [newUnblindKey, setNewUnblindKey] = useState(0);

  // Build dropdown options for managers (all invites, excluding those who already have assessments)
  const availableBlindAssessors: SelectOption[] = assessorInvitations
    .filter(inv => !blinded.some(a => a.assessorId === inv.inviteeId))
    .map(inv => ({
      value: inv.inviteeId,
      label: parseNameFromEmail(inv.inviteeEmail) || inv.inviteeEmail
    }));

  const availableUnblindAssessors: SelectOption[] = assessorInvitations
    .filter(inv => !unblinded.some(a => a.assessorId === inv.inviteeId))
    .map(inv => ({
      value: inv.inviteeId,
      label: parseNameFromEmail(inv.inviteeEmail) || inv.inviteeEmail
    }));

  const userBlind = blinded.find((a: Assessment) => a.assessorId === currentUserId);
  const userUnblind = unblinded.find((a: Assessment) => a.assessorId === currentUserId);
  // Only show other assessors' entries after current user has one
  const blindedToShow = userBlind ? blinded : [];
  const unblindedToShow = userUnblind ? unblinded : [];

  const completedBlind = blinded.filter((a: Assessment) => a.status === AssessmentStatus.COMPLETED).length;
  const completedUnblind = unblinded.filter((a: Assessment) => a.status === AssessmentStatus.COMPLETED).length;

  if (isLoading) {
    return (
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Assessments</h2>
        <div className="flex items-center justify-center py-8">
          <Spinner className="h-8 w-8" />
          <span className="ml-2">Loading assessments...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Assessments</h2>
        <div className="bg-red-50 text-red-800 p-4 rounded-md mt-4">
          <h3 className="text-lg font-semibold">Error loading assessments</h3>
          <p>{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 space-y-8">
      {/* Blinded Phase */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Blinded Assessments</h2>
          <Badge>{completedBlind} of {invitedCount}</Badge>
        </div>

        {blinded.length === 0 && !isLoading ? (
          <Skeleton className="h-4 w-3/4" />
        ) : (
          blindedToShow.map((item: Assessment) => (
            <AssessmentItem
              key={item.id}
              item={item}
              currentUserId={currentUserId}
              isEditable={isEditable}
              isManager={isManager}
              updateAssessment={updateAssessment}
              deleteAssessment={deleteAssessment}
              isUpdating={isUpdating}
              isDeleting={isDeleting}
            />
          ))
        )}

        {/* New blind assessment form */}
        {isEditable && (
          (!isManager && !userBlind) || 
          (isManager && availableBlindAssessors.length > 0)
        ) && (
          <div className="py-6 space-y-4 border-b last:border-b-0">
            <AssessmentForm
              key={newBlindKey}
              onSubmit={(data) =>
                createAssessment({ 
                  ...data, 
                  packetId: packetId, 
                  type: AssessmentType.BLINDED,
                  ...(isManager ? { assessorId: selectedBlindAssessorId } : {})
                }, { onSuccess: () => setNewBlindKey(k => k + 1) })
              }
              onCancel={() => setNewBlindKey(k => k + 1)}
              isSubmitting={isCreating}
              isManager={isManager}
              availableAssessors={availableBlindAssessors}
              selectedAssessorId={selectedBlindAssessorId}
              onAssessorChange={setSelectedBlindAssessorId}
            />
          </div>
        )}
      </section>

      {/* Unblinded Phase */}
      <section className={`relative ${completedBlind < invitedCount ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Unblinded Assessments</h2>
          <Badge>{completedUnblind} of {invitedCount}</Badge>
        </div>

        {completedBlind < invitedCount ? (
          <div className="text-sm text-muted-foreground">
            Unblinded assessments are locked until all blinded assessments complete.
          </div>
        ) : unblinded.length === 0 ? (
          <Skeleton className="h-4 w-3/4" />
        ) : (
          unblindedToShow.map((item: Assessment) => (
            <AssessmentItem
              key={item.id}
              item={item}
              currentUserId={currentUserId}
              isEditable={isEditable}
              isManager={isManager}
              updateAssessment={updateAssessment}
              deleteAssessment={deleteAssessment}
              isUpdating={isUpdating}
              isDeleting={isDeleting}
            />
          ))
        )}

        {/* New unblinded form */}
        {isEditable && completedBlind === invitedCount && (
          (!isManager && !userUnblind) || 
          (isManager && availableUnblindAssessors.length > 0)
        ) && (
          <div className="py-6 space-y-4 border-b last:border-b-0">
            <AssessmentForm
              key={newUnblindKey}
              onSubmit={(data) =>
                createAssessment({ 
                  ...data, 
                  packetId: packetId, 
                  type: AssessmentType.UNBLINDED,
                  ...(isManager ? { assessorId: selectedUnblindAssessorId } : {})
                }, { onSuccess: () => setNewUnblindKey(k => k + 1) })
              }
              onCancel={() => setNewUnblindKey(k => k + 1)}
              isSubmitting={isCreating}
              isManager={isManager}
              availableAssessors={availableUnblindAssessors}
              selectedAssessorId={selectedUnblindAssessorId}
              onAssessorChange={setSelectedUnblindAssessorId}
            />
          </div>
        )}
      </section>
    </div>
  );
}

// Export AssessmentItem for reuse in split sections
export { AssessmentItem }; 